import React, { useState, useEffect, useContext } from "react";
import { Form, Button } from "react-bootstrap";
import axios from "axios";
import useCookie from "../../hooks/useCookie";
import useCurrentUser from "../../hooks/useCurrentUser";
import { EntityManagementContext } from "../../context/EntityManagementContext";

// Create new or edit existing student entity
function ManageAdminForm(props) {
  const {
    editingID,
    exit,
    mode,
    setMode,
    updateEntities
  } = useContext(EntityManagementContext);

  // Value of form elements; used to load data in edit mode
  // and grabbed upon save
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState(""); // Have user enter password twice to confirm
  const [editingPassword, setEditingPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  // Do password 1 and 2 match?
  const [passwordMismatch, setPasswordMismatch] = useState(false);

  // When set to true, displays an error badge that helps
  // user correct their error.
  const [errorState, setErrorState] = useState(false);

  // List of errors to display after creation/editing error
  // occurs; implemented as dictionary mapping input field
  // to associated list of error messages.
  const [errorDict, setErrorDict] = useState({});

  const csrftoken = useCookie("csrftoken");

  const user = useCurrentUser();

  // Badge that appears upon sucessful creation/edit of class
  const successBadge = () => {
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
    }, 5000);
  };

  // If an admin has been selected to edit, populate
  // all form options except password with currently
  // saved data
  useEffect(() => {
    if (mode == "Edit") {
      // Clear any error messages from previous
      // operations.
      setErrorState(false);
      setErrorDict({});

      axios.get("/api/admins/" + editingID)
        .then((response) => {
          let editingAdmin = response.data;
          setFirstName(editingAdmin.first_name);
          setLastName(editingAdmin.last_name);
          setEmail(editingAdmin.email);
          // No password should appear when user edits this field; 
          // they must set a new one.
          setPassword("");
        });
    } else if (mode === "Create") {
      // Clear any error messages from previous
      // operations.
      setErrorState(false);
      setErrorDict({});

      // Clear form
      clearForm();
    }
  }, [editingID]);

  // Clear all form values
  const clearForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setPassword2("");
  }

  // Cancel creation or editing of student
  const cancelAction = () => {
    if (mode == "Edit") {
      setMode("Create");
    }
    clearForm();
    exit();
  }

  // Organize validation error information for error badge display.
  //  errorObj: HTTP response carrying error information, as JSON object
  const handleFieldValidationErrors = (errorObj) => {
    // Gather errors, which will we use to update state at the end
    let currentErrorDict = {};

    // Get response body
    let errorBody = errorObj.response.data;

    // This is MVP - just handling errors from server-side validation
    // Would be better if we had client-side validation as well to make 
    // the user registration process faster

    // Handle first name related errors
    if (errorBody["first_name"] && errorBody["first_name"].length >= 1) {
      currentErrorDict["First name"] = errorBody["first_name"];
    }

    // Handle last name related errors
    if (errorBody["last_name"] && errorBody["last_name"].length >= 1) {
      currentErrorDict["Last name"] = errorBody["last_name"];
    }

    // Handle email (username) related errors
    if (errorBody["email"] && errorBody["email"].length >= 1) {
      currentErrorDict["Username"] = errorBody["email"];
    }

    setErrorDict(currentErrorDict);
    setErrorState(true);
  }

  // Submission of form
  // Depending on current mode, creates or saves entity to database
  const handleSubmit = async (e) => {
    // Prevent default behavior - do not clear inputs immediately
    e.preventDefault();

    // Clear error state
    setErrorState(false);
    setErrorDict({});

    const headers = {
      "X-CSRFToken": csrftoken,
    };

    // Check that passwords match before
    // submitting
    if (password != password2) {
      setPasswordMismatch(true);
      return;
    } else {
      setPasswordMismatch(false);
    }

    // Use form values to create new entity in database
    if (mode == "Create") {
      // Create two galleries for the new student
      const gallery1 = await axios.post(
        "/api/galleries/",
        {
          name: "Recent Gallery",
        },
        { headers }
      );
      const gallery2 = await axios.post(
        "/api/galleries/",
        {
          name: "Saved Gallery",
        },
        { headers }
      );
      const newAdmin = {
        first_name: firstName,
        last_name: lastName,
        email: email,
        password: password,
        recent: gallery1.data.galleryID,
        saved: gallery2.data.galleryID,
      };

      axios
        .post("/api/admins/", newAdmin, { headers })
        .then((response) => {
          if (response.status === 201) {
            successBadge();
            // Clear form values
            clearForm();
            // Update table with new admin
            updateEntities(response.data);
          }
        })
        .catch(error => {
          handleFieldValidationErrors(error);
        });
    } else if (mode == "Edit") { // Update existing entity in database
      const updatedAdmin = {
        first_name: firstName,
        last_name: lastName,
        email: email,
      };

      if (password != null && password != "") {
        updatedAdmin["password"] = password;
      }

      axios.patch("/api/admins/" + editingID, updatedAdmin, { headers })
        .then((response) => {
          if (response.status == 200) {
            successBadge();
            // Clear form values
            clearForm();
            // Set mode back to create
            setMode("Create");
            // Update table with updated admin
            updateEntities(response.data, editingPassword);
          }
        })
        .catch(error => {
          handleFieldValidationErrors(error);
        });
    }
  };

  const editPassword = () => {
    setEditingPassword(true);
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group controlId="formBasicFirstName">
        <Form.Label>First Name</Form.Label> {/* Form item: First name */}
        <Form.Control
          type="text"
          placeholder="Enter first name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
        />
      </Form.Group>

      <Form.Group controlId="formBasicLastName">
        <Form.Label>Last Name</Form.Label> {/* Form item: Last name */}
        <Form.Control
          type="text"
          placeholder="Enter last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
        />
      </Form.Group>

      <Form.Group controlId="formBasicEmail">
        <Form.Label>Username</Form.Label> {/* Form item: Email */}
        <Form.Control
          type="text"
          placeholder="Enter username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </Form.Group>

      <Form.Group controlId="formBasicPassword">
        <Form.Label>Password</Form.Label> {/* Form item: Password */}
        {editingPassword || mode == "Create" ? (
          <>
            {/* Password input */}
            <Form.Control
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {/* Confirm password input */}
            <Form.Control
              type="password"
              placeholder="Confirm password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              required
            />
            {passwordMismatch ? (
              <p id="password-mismatch">Passwords do not match.</p>
            ) : (<></>)}
          </>
        ) : (
          <button className="admin-button" onClick={editPassword}>
            Change Password
          </button>
        )}
        {user && user.id == editingID && editingPassword ? (
          <p id="self-edit-disclaimer">
            WARNING: You will be logged out when you change your password.
          </p>
        ) : (<></>)}
      </Form.Group>

      {/* Submit button */}
      <Button id="addAdmin" className="admin-button" variant="primary" type="submit">
        {mode} Admin
      </Button>

      {/* Cancel button */}
      <Button
        className="admin-button"
        onClick={cancelAction}
        style={{ marginTop: "5px" }}
      >
        Cancel
      </Button>

      {/* Show success badge if action was successful */}
      {success ? (
        <div
          className="alert alert-success"
          role="alert"
          style={{ marginTop: "5px" }}
        >
          Successful!
        </div>
      ) : null}

      {/* If there are any erroneous inputs, let the user know */}
      {errorState ? (
        <div
          className="alert alert-danger"
          role="alert"
          style={{ marginTop: "5px" }}
        >
          <strong>An error occurred.</strong><br /><br />
          {Object.entries(errorDict).map(
            (field) =>
              <>
                <i>{field[0]}</i>
                <ul>
                  {field[1].map((error) =>
                    <li>{error}</li>
                  )}
                </ul>
              </>
          )}
        </div>
      ) : null}
    </Form>
  );
}

export default ManageAdminForm;
